import { useEffect, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { Select } from '@/shared/ui/components/form/inputs';
import { MaskedPercentageField } from './MaskedNumberField';
import { OwnerClientPicker } from './OwnerClientPicker';
import { OwnershipAttributeFields } from './OwnershipAttributeFields';
import { MANDATE_TYPES } from './schema';
import { useAgencyRequired } from './agency-required-context';
import type { PropertyFormValues } from './schema';
import { DateField } from '@/shared/ui/components/form/inputs';

interface OwnershipSectionProps {
  form: UseFormReturn<PropertyFormValues>;
  agencyId: string;
  /** Opens the section, as when the drawer was opened from the ownership card's pencil. */
  expand?: boolean;
}

/**
 * Renders the property ownership and mandate section.
 * Owner is selected via searchable autocomplete with quick-create.
 * Collapsed by default, and opened whenever `expand` turns true.
 */
export function OwnershipSection({ form, agencyId, expand = false }: OwnershipSectionProps) {
  const { t } = useTranslation('features/properties');
  const ownerRequired = useAgencyRequired('owner_client_id');
  const ownerError = form.formState.errors.owner_client_id?.message;
  // A required owner is not something to tuck away: the section opens so
  // the broker sees the field, and again when a save is blocked on it.
  const open = expand || ownerRequired || !!ownerError;
  const [collapsed, setCollapsed] = useState(!open);

  useEffect(() => {
    if (open) setCollapsed(false);
  }, [open]);

  const mandateOptions = MANDATE_TYPES.map((m) => ({
    value: m,
    label: t(`form.mandate.${m}`, m),
  }));

  return (
    <FormSection
      title={t('propertyDrawer.sections.ownership', 'Ownership')}
      collapsed={collapsed}
      onToggle={() => setCollapsed((prev) => !prev)}
    >
      <Controller
        name="owner_client_id"
        control={form.control}
        render={({ field }) => (
          <OwnerClientPicker
            agencyId={agencyId}
            value={field.value ?? null}
            onChange={(id) => field.onChange(id ?? undefined)}
            required={ownerRequired}
            error={ownerError}
          />
        )}
      />

      <Controller
        name="mandate_type"
        control={form.control}
        render={({ field }) => (
          <Select
            label={t('propertyDrawer.fields.mandateType', 'Mandate type')}
            data={mandateOptions}
            value={field.value ?? null}
            onChange={(v) => field.onChange(Array.isArray(v) ? v[0] : v)}
            placeholder={t('propertyDrawer.fields.mandateTypePlaceholder', 'Select...')}
          />
        )}
      />

      <div className="grid grid-cols-2 gap-(--space-3)">
        <Controller
          name="mandate_start_date"
          control={form.control}
          render={({ field }) => (
            <DateField
              label={t('propertyDrawer.fields.mandateStartDate', 'Start date')}
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="mandate_end_date"
          control={form.control}
          render={({ field }) => (
            <DateField
              label={t('propertyDrawer.fields.mandateEndDate', 'End date')}
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <Controller
        name="mandate_commission"
        control={form.control}
        render={({ field }) => (
          <MaskedPercentageField
            label={t('propertyDrawer.fields.mandateCommission', 'Commission %')}
            suffix="%"
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <OwnershipAttributeFields form={form} agencyId={agencyId} />
    </FormSection>
  );
}
